// The single arbiter of the prompts (script/personas/pilotage.md §4): each one claims the screen before
// showing (claim) and gives it back once gone (release). Priority: paywall > account gate > save reminder >
// feature spotlight > draft resume > onboarding > feedback question.
// - The paywall and the account gate are never refused (conversion first): they take the place of a lower
//   prompt on screen (its onPreempt hides it).
// - The others are refused over another prompt on screen (the onboarding, the home's tour, only holds back
//   those below it), within SPACING_MS of another prompt, while the visitor draws, and past one asking modal
//   per visit. The feedback question also keeps its own: once per visit, never over the save form.
// - A tip (the feature spotlight) gives way: a prompt above it takes its place, and it spends no spacing.
// - The interventions' holdout (Orchestrator.active() false) keeps today's behaviour: nothing is refused.
// Each decision is tracked (prompt_decision), a refusal once per page and reason.
import * as Utils from '@js/modules/utils';
import UsageTotals from '@js/modules/usageTotals';
import AnalyticsService from '@js/modules/analyticsService';
import Orchestrator from '@js/modules/orchestrator';

const KEY = 'prompt-budget';
const SPACING_MS = 60 * 1000;
const TYPING_MS = 5000;
const DRAWING_MS = 3000;
const PRIORITY = ['paywall', 'account_gate', 'save_nudge', 'spotlight', 'draft', 'onboarding', 'feedback'];
const CONVERSION = ['paywall', 'account_gate'];
const ASKING_MODALS = ['save_nudge', 'feedback'];
const AMBIENT = 'onboarding';
const TIPS = ['spotlight'];

// Storage unavailable (blocked, private mode) or full: the budget lives in the page.
let pageState = {};
let writeFailed = false;
// The prompts on screen in this page: kind → {isOpen, onPreempt}.
const held = new Map();
const logged = new Set();

// One state per Ahoy visit: a new visit (12 h of inactivity) starts with a fresh budget.
const read = () => {
    const visit = Utils.getCookie('ahoy_visit') || '';
    let state;
    try {
        state = writeFailed ? pageState : JSON.parse(window.sessionStorage.getItem(KEY) || '{}');
    } catch {
        state = pageState;
    }

    return state.visit === visit ? state : {visit};
};

const write = (state) => {
    pageState = state;
    try {
        window.sessionStorage.setItem(KEY, JSON.stringify(state));
        writeFailed = false;
    } catch {
        writeFailed = true;
    }
};

const log = (kind, decision, reason = null) => {
    AnalyticsService.trackPromptDecision?.(kind, decision, reason);
};

// A prompt whose isOpen says it is gone frees its place by itself (a modal closed outside its owner).
const onScreen = () => [...held.keys()].filter((kind) => {
    const open = held.get(kind).isOpen?.() ?? true;
    if (!open) {
        held.delete(kind);
    }

    return open;
});

const refused = (reason, wait = null) => ({ok: false, reason, wait});
const GRANTED = {ok: true, reason: null, wait: null};

const PromptBudget = {
    // {ok, reason, wait}: wait (ms) when a later claim may pass. dryRun decides without taking the place.
    claim(kind, {isOpen = null, onPreempt = null, dryRun = false, moment = null} = {}) {
        const holdout = Orchestrator.active?.() === false;
        const decision = holdout ? GRANTED : this.decide(kind, {moment});
        if (!decision.ok) {
            if (!logged.has(`${kind}:${decision.reason}`)) {
                logged.add(`${kind}:${decision.reason}`);
                log(kind, 'refused', decision.reason);
            }

            return decision;
        }
        if (dryRun) {
            return decision;
        }

        if (!holdout) {
            onScreen().filter((other) => (CONVERSION.includes(kind) && !CONVERSION.includes(other) && other !== AMBIENT) || this.yields(other, kind)).forEach((other) => {
                const {onPreempt: hide} = held.get(other);
                held.delete(other);
                hide?.();
                log(other, 'preempted', kind);
            });
        }

        held.set(kind, {isOpen, onPreempt});
        const now = Date.now();
        write({
            ...read(), last: now, lastKind: kind,
            ...(kind === AMBIENT || TIPS.includes(kind) ? {} : {loud: now}),
            ...(ASKING_MODALS.includes(kind) ? {modal: kind} : {}),
            ...(kind === 'feedback' ? {asked: true} : {})
        });
        log(kind, 'shown', holdout ? 'holdout' : null);

        return decision;
    },

    release(kind) {
        held.delete(kind);
    },

    // A tip on screen gives way to a prompt above it.
    yields(other, kind) {
        return TIPS.includes(other) && PRIORITY.indexOf(kind) < PRIORITY.indexOf(other);
    },

    // The rules alone, nothing taken nor tracked.
    decide(kind, {moment = null} = {}) {
        if (CONVERSION.includes(kind) || kind === AMBIENT) {
            return GRANTED;
        }

        const rank = PRIORITY.indexOf(kind);
        if (onScreen().some((other) => other !== kind && !this.yields(other, kind) && (other !== AMBIENT || PRIORITY.indexOf(other) < rank))) {
            return refused('busy');
        }

        const state = read();
        if (kind === 'feedback' && state.asked) {
            return refused('asked');
        }
        // The save reminder often leads to the save: the question after it is still asked (#681).
        const afterReminder = kind === 'feedback' && moment === 'after_save' && state.modal === 'save_nudge';
        if (ASKING_MODALS.includes(kind) && state.modal && !afterReminder) {
            return refused('modal_spent');
        }

        // The question waited after the onboarding's steps too, as before.
        const since = kind === 'feedback' ? state.last : state.loud;
        const spacing = since ? SPACING_MS - (Date.now() - since) : 0;
        if (spacing > 0) {
            return refused('spacing', spacing);
        }

        const quiet = (kind === 'feedback' ? TYPING_MS : DRAWING_MS) - (performance.now() - UsageTotals.lastInputAt());
        if (quiet > 0) {
            return refused('drawing', Math.ceil(quiet));
        }

        return kind === 'feedback' && document.getElementById('ride-creation') ? refused('save_form') : GRANTED;
    },

    // A prompt shown outside the arbiter (an onboarding step): the question waits after it.
    record(kind) {
        write({...read(), last: Date.now(), lastKind: kind});
    },

    // The question opened by the visitor (the pill).
    markAsked() {
        write({...read(), asked: true, last: Date.now(), lastKind: 'feedback'});
    },

    // Milliseconds before the spacing allows a question; null once the visit was asked.
    delayBeforeAsk() {
        const state = read();
        if (state.asked) {
            return null;
        }

        return state.last ? Math.max(0, SPACING_MS - (Date.now() - state.last)) : 0;
    },

    canAsk() {
        return this.decide('feedback').ok;
    },

    // Tests only.
    reset() {
        held.clear();
        logged.clear();
        pageState = {};
        writeFailed = false;
    }
};

export default PromptBudget;
